<template>
    <div class="checkout__step-3" data-test="checkout-step-3">
        <div
            data-track-name="lifetime-updates-modal"
            class="lifetime-updates"
            :class="{
                'lifetime-updates--loaded': loaded,
                'lifetime-updates--standalone': standalone,
                'lifetime-updates--thank-you': lifetimeUpdates?.thankYou,
            }">
            <template v-if="lifetimeUpdates?.thankYou ?? false">
                <div class="lifetime-updates__title">
                    {{ $t('lifetimeUpdates.thankYou') }}!<br />
                    {{ $t('lifetimeUpdates.successfulPurchase') }}
                </div>
            </template>
            <template v-else>
                <div class="lifetime-updates__subtitle">{{ $t('lifetimeUpdates.subtitle') }}</div>
                <div class="lifetime-updates__title">
                    <div class="lifetime-updates__title-smaller">
                        <div v-html="`${$t('lifetimeUpdates.newText1')} ${productTitle} ${$t('lifetimeUpdates.newText2')}`"></div>
                    </div>
                    <img
                        src="https://images.iphonephotographyschool.com/33883/Group-717.svg"
                        alt="arrow"
                        class="lifetime-updates__arrow" />
                </div>

                <div class="lifetime-updates__price">{{ $t('lifetimeUpdates.only') }} {{ price }}</div>

                <immediate-payment-button
                    v-if="!isIosView"
                    :payment-type="'now'"
                    :apple-pay="defaultPaymentMethod === 'apple_pay' ? true : false"
                    :classes="`lifetime-updates__button checkout__button ${processingCheckout ? '' : 'pulse'}`"
                    :isLifetimeUpdates="true">
                    {{ $t('lifetimeUpdates.button') }}
                </immediate-payment-button>
                <button
                    v-if="isIosView"
                    class="buy-button buy-button--has-payment-method lifetime-updates__button checkout__button pulse"
                    @click="handleIosViewBuyButtonClicked">
                    {{ $t('lifetimeUpdates.button') }}
                </button>
                <a
                    href="javascript:"
                    class="lifetime-updates__no-thanks"
                    data-test="lifetime-updates-no-thanks-button"
                    @click="handleNoThanks"
                    >{{ $t('lifetimeUpdates.noThanks') }}</a
                >
            </template>
        </div>
    </div>
</template>

<script>
import ImmediatePaymentButton from './buttons/ImmediatePaymentButton.vue';
import axios from 'axios';
import msg from '../../messages';
import { mapGetters, mapState } from 'vuex';
import { iosview } from '../../iosview';

export default {
    components: {
        ImmediatePaymentButton,
    },
    props: {
        standalone: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            loaded: false,
            failed_fetch_price_call_count: 0,
            iosviewLuPrice: new URLSearchParams(window.location.search).get('luPrice'),
            productTitle: '',
        };
    },
    computed: {
        ...mapGetters(['product']),
        ...mapState([
            'lifetimeUpdates',
            'defaultPaymentMethod',
            'processingCheckout',
            'initialCurrency',
            'sessionId',
            'originalUrl',
            'originalDate',
            'enforceHardDisplay',
        ]),
        price() {
            if (this.isIosView && this.iosviewLuPrice) {
                return this.iosviewLuPrice;
            }
            return this.standalone
                ? this.product.price.lu?.display_value[this.enforceHardDisplay ? 'hard' : 'soft']
                : this.lifetimeUpdates.price;
        },
    },
    methods: {
        fetchPrices() {
            logger.addCheckoutBreadcrumb({
                message: msg.checkout.fetchPricesStarted,
                data: { attempt: this.failed_fetch_price_call_count },
            });

            return axios
                .post(
                    '/api/fetch-prices',
                    {
                        currency: this.initialCurrency,
                        psh: this.sessionId,
                        original_url: this.originalUrl,
                        original_date: this.originalDate,
                        ...(this.standalone ? {} : { product_slug: this.lifetimeUpdates.productSlug }),
                    },
                    {
                        headers: {
                            'x-transaction-id': logger.getTransactionId(),
                        },
                    },
                )
                .then((response) => {
                    if (!response.data.currency || !response.data.product) {
                        return this.retryFetchPrices();
                    }

                    if (this.standalone) {
                        this.$store.commit('setCurrency', {
                            currency: response.data.currency,
                            enforceHardDisplay: response.data.enforce_hard_display,
                        });
                        this.$store.commit('setProduct', response.data.product);
                        this.$store.commit('setIsSca', response.data.is_sca);
                    } else {
                        this.$store.commit('setLifetimeUpdates', {
                            ...this.lifetimeUpdates,
                            price: this.enforceHardDisplay
                                ? response.data.product.price.lu.display_value.hard
                                : response.data.product.price.lu.display_value.soft,
                        });
                    }

                    this.productTitle = response.data.product_title;

                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.fetchPricesCompleted,
                        data: {
                            attempt: this.failed_fetch_price_call_count,
                            currency: response.data.currency,
                            product: response.data.product,
                        },
                    });

                    this.loaded = true;
                    this.$store.commit('setProcessingCheckout', false);
                })
                .catch((err) => {
                    this.$store.commit('setProcessingCheckout', false);

                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.fetchPricesTryFailed,
                        data: {
                            attempt: this.failed_fetch_price_call_count,
                            err,
                        },
                    });

                    this.retryFetchPrices();
                });
        },

        retryFetchPrices() {
            if (++this.failed_fetch_price_call_count < 5) {
                setTimeout(() => {
                    this.fetchPrices();
                }, 3000);
            } else {
                logger.captureException(new Error(msg.checkout.fetchPricesFailed));
            }
        },
        handleIosViewBuyButtonClicked() {
            if (window.App.isIosPwa) {
                this.$store.commit('setProcessingCheckout', true);
            }
            iosview.lifetimeUpdatesButtonClicked();
        },
        handleNoThanks(event) {
            if (this.isIosView) {
                iosview.skipLifetimeUpdatesButtonClicked();
                return;
            }

            window.ipse.dispatch({
                action: 'no_thanks_lifetime_updates_clicked',
                data_event: {
                    section_name:
                        event.srcElement.closest('[data-track-name]')?.getAttribute('data-track-name') ?? 'unknown',
                },
            });

            window.location.href = this.lifetimeUpdates.redirectUrl;
        },
    },
    mounted() {
        if (this.isIosView) {
            this.loaded = true;
            return;
        }
        this.fetchPrices();
        if (this.standalone) {
            this.$store.commit('setLifetimeUpdates', { standalone: true });
        }
    },
};
</script>
